import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { PageSkeleton } from '@/components/shared/PageSkeleton';

/**
 * Every Inventory route in one place, so the feature plugs into the app — or out of it — with a single import.
 * Paths: /inventory (dashboard), /inventory/jobs/* (Job Materials), /inventory/stock/* (Stock), /inventory/purchasing/* (Purchasing),
 * /inventory/assets/* (Assets), /inventory/reports/* (Reports), /inventory/alerts, /settings/inventory/* (setup).
 */
const InventoryDashboardPage = lazy(() => import('./pages/InventoryDashboardPage'));
const CurrentStockPage = lazy(() => import('./pages/CurrentStockPage'));
const StoresPage = lazy(() => import('./pages/StoresPage'));
const StoreDetailPage = lazy(() => import('./pages/StoreDetailPage'));
const DrumsPage = lazy(() => import('./pages/DrumsPage'));
const DrumDetailPage = lazy(() => import('./pages/DrumDetailPage'));
const ItemMasterPage = lazy(() => import('./setup/ItemMasterPage'));
const RulesPage = lazy(() => import('./setup/RulesPage'));
const OpeningStockPage = lazy(() => import('./setup/OpeningStockPage'));
const VendorsPage = lazy(() => import('./setup/VendorsPage'));
const PurchaseOrdersPage = lazy(() => import('./pages/purchasing/PurchaseOrdersPage'));
const PurchaseOrderDetailPage = lazy(() => import('./pages/purchasing/PurchaseOrderDetailPage'));
const GrnsPage = lazy(() => import('./pages/purchasing/GrnsPage'));
const GrnDetailPage = lazy(() => import('./pages/purchasing/GrnDetailPage'));
const SerialImportPage = lazy(() => import('./pages/purchasing/SerialImportPage'));
const JobQueuePage = lazy(() => import('./pages/jobs/JobQueuePage'));
const JobMaterialPage = lazy(() => import('./pages/jobs/JobMaterialPage'));
const ReturnsPage = lazy(() => import('./pages/jobs/ReturnsPage'));
const VariancesPage = lazy(() => import('./pages/jobs/VariancesPage'));
const CustodyPage = lazy(() => import('./pages/jobs/CustodyPage'));
const SubscriberAssetsPage = lazy(() => import('./pages/assets/SubscriberAssetsPage'));
const SubscriberAssetDetailPage = lazy(() => import('./pages/assets/SubscriberAssetDetailPage'));
const PopAssetsPage = lazy(() => import('./pages/assets/PopAssetsPage'));
const TestingRmaPage = lazy(() => import('./pages/assets/TestingRmaPage'));
const DamagedScrapPage = lazy(() => import('./pages/assets/DamagedScrapPage'));
const TransfersPage = lazy(() => import('./pages/stock/TransfersPage'));
const TransferDetailPage = lazy(() => import('./pages/stock/TransferDetailPage'));
const PartnerStockPage = lazy(() => import('./pages/stock/PartnerStockPage'));
const AuditsPage = lazy(() => import('./pages/stock/AuditsPage'));
const AuditDetailPage = lazy(() => import('./pages/stock/AuditDetailPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const AlertsPage = lazy(() => import('./pages/AlertsPage'));
const AlertsSettingsPage = lazy(() => import('./setup/AlertsSettingsPage'));

const page = (el: ReactNode) => <Suspense fallback={<PageSkeleton />}>{el}</Suspense>;

export const inventoryRoutes: RouteObject[] = [
  { path: '/inventory', element: page(<InventoryDashboardPage />) },
  { path: '/inventory/jobs', element: <Navigate to="/inventory/jobs/issue" replace /> },
  { path: '/inventory/jobs/issue', element: page(<JobQueuePage />) },
  { path: '/inventory/jobs/issue/:job', element: page(<JobMaterialPage />) },
  { path: '/inventory/jobs/returns', element: page(<ReturnsPage />) },
  { path: '/inventory/jobs/variances', element: page(<VariancesPage />) },
  { path: '/inventory/jobs/custody', element: page(<CustodyPage />) },
  { path: '/inventory/stock', element: page(<CurrentStockPage />) },
  { path: '/inventory/stock/stores', element: page(<StoresPage />) },
  { path: '/inventory/stock/stores/:id', element: page(<StoreDetailPage />) },
  { path: '/inventory/stock/drums', element: page(<DrumsPage />) },
  { path: '/inventory/stock/drums/:code', element: page(<DrumDetailPage />) },
  { path: '/inventory/stock/transfers', element: page(<TransfersPage />) },
  { path: '/inventory/stock/transfers/:no', element: page(<TransferDetailPage />) },
  { path: '/inventory/stock/partners', element: page(<PartnerStockPage />) },
  { path: '/inventory/stock/partners/:partnerId', element: page(<PartnerStockPage />) },
  { path: '/inventory/stock/audits', element: page(<AuditsPage />) },
  { path: '/inventory/stock/audits/:no', element: page(<AuditDetailPage />) },
  { path: '/inventory/reports', element: page(<ReportsPage />) },
  { path: '/inventory/reports/:key', element: page(<ReportsPage />) },
  { path: '/inventory/alerts', element: page(<AlertsPage />) },
  { path: '/inventory/purchasing', element: <Navigate to="/inventory/purchasing/orders" replace /> },
  { path: '/inventory/purchasing/orders', element: page(<PurchaseOrdersPage />) },
  { path: '/inventory/purchasing/orders/:id', element: page(<PurchaseOrderDetailPage />) },
  { path: '/inventory/purchasing/grns', element: page(<GrnsPage />) },
  { path: '/inventory/purchasing/grns/:id', element: page(<GrnDetailPage />) },
  { path: '/inventory/purchasing/import', element: page(<SerialImportPage />) },
  { path: '/inventory/assets', element: <Navigate to="/inventory/assets/subscribers" replace /> },
  { path: '/inventory/assets/subscribers', element: page(<SubscriberAssetsPage />) },
  { path: '/inventory/assets/subscribers/:sub', element: page(<SubscriberAssetDetailPage />) },
  { path: '/inventory/assets/pops', element: page(<PopAssetsPage />) },
  { path: '/inventory/assets/pops/:pop', element: page(<PopAssetsPage />) },
  { path: '/inventory/assets/testing', element: page(<TestingRmaPage />) },
  { path: '/inventory/assets/damaged', element: page(<DamagedScrapPage />) },
  // The Classic CPE screen was retired when Assets replaced it; old bookmarks land on Subscriber Assets.
  { path: '/inventory/cpe', element: <Navigate to="/inventory/assets/subscribers" replace /> },
  { path: '/settings/inventory', element: <Navigate to="/settings/inventory/items" replace /> },
  { path: '/settings/inventory/items', element: page(<ItemMasterPage />) },
  { path: '/settings/inventory/vendors', element: page(<VendorsPage />) },
  { path: '/settings/inventory/rules', element: page(<RulesPage />) },
  { path: '/settings/inventory/alerts', element: page(<AlertsSettingsPage />) },
  { path: '/settings/inventory/opening-stock', element: page(<OpeningStockPage />) },
];
